import { encode, decode } from '@googlemaps/polyline-codec';
import {
  FilterCoordinates,
  PolygonFilter,
  PolygonPath,
  PolygonPoint,
} from 'components/UnitsFilters/types';

// Precision 5 = Google's polyline standard, ~1.1m resolution —
// plenty for a hand-drawn search boundary.
const POLYLINE_PRECISION = 5;

// Matches cbb-api's MAX_POLYGON_VERTICES sanity limit — a ring above this
// would be silently dropped server-side, so reject it before it gets there.
export const MAX_POLYGON_VERTICES = 200;

export const roundCoordinates = (
  coordinates: FilterCoordinates,
  decimals = 6
) => {
  return {
    southWest: {
      lat: +coordinates?.southWest?.lat?.toFixed(decimals),
      lng: +coordinates?.southWest?.lng?.toFixed(decimals),
    },
    northEast: {
      lat: +coordinates?.northEast?.lat?.toFixed(decimals),
      lng: +coordinates?.northEast?.lng?.toFixed(decimals),
    },
  };
};

export const formatCoordinates = (
  sw: string[],
  ne: string[]
): FilterCoordinates => {
  return {
    southWest: {
      lat: +sw[0],
      lng: +sw[1],
    },
    northEast: {
      lat: +ne[0],
      lng: +ne[1],
    },
  };
};

export const roundPolygons = (
  polygons: PolygonFilter,
  decimals = 6
): PolygonFilter => {
  return (polygons || []).map((path) =>
    path.map((point) => ({
      lat: +point?.lat?.toFixed(decimals),
      lng: +point?.lng?.toFixed(decimals),
    }))
  );
};

export const formatPolygons = (poly: string): PolygonFilter => {
  if (!poly) return [];

  return poly.split(';').map((ring) => {
    // Legacy "lat,lng|lat,lng" format is detected by the comma — polyline
    // output never contains one. Keeps old bookmarks/saved searches working.
    if (ring.includes(',')) {
      return ring.split('|').map((point) => {
        const [lat, lng] = point.split(',');
        return { lat: +lat, lng: +lng };
      });
    }
    return decode(ring, POLYLINE_PRECISION).map(([lat, lng]) => ({
      lat,
      lng,
    }));
  });
};

export const stringifyPolygons = (polygons: PolygonFilter): string => {
  return (polygons || [])
    .map((path) =>
      encode(
        path.map((p) => [p.lat, p.lng]),
        POLYLINE_PRECISION
      )
    )
    .join(';');
};

// Drops points within `minDistance` of the last kept point — hand tremor
// can fold a sample back into a self-intersection otherwise.
export const thinPolygonPath = (
  path: PolygonPath,
  minDistance: number
): PolygonPath => {
  if (!path.length) return path;

  const result = [path[0]];
  for (let i = 1; i < path.length; i++) {
    const last = result[result.length - 1];
    const point = path[i];
    if (Math.hypot(point.lat - last.lat, point.lng - last.lng) >= minDistance) {
      result.push(point);
    }
  }
  return result;
};

// Perpendicular distance from `point` to the line through `lineStart`/
// `lineEnd` (or point-to-point if they coincide). Used below.
const perpendicularDistance = (
  point: PolygonPoint,
  lineStart: PolygonPoint,
  lineEnd: PolygonPoint
): number => {
  const dx = lineEnd.lng - lineStart.lng;
  const dy = lineEnd.lat - lineStart.lat;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return Math.hypot(point.lat - lineStart.lat, point.lng - lineStart.lng);
  }
  const t =
    ((point.lng - lineStart.lng) * dx + (point.lat - lineStart.lat) * dy) /
    lengthSquared;
  const clampedT = Math.max(0, Math.min(1, t));
  const projectedLng = lineStart.lng + clampedT * dx;
  const projectedLat = lineStart.lat + clampedT * dy;
  return Math.hypot(point.lat - projectedLat, point.lng - projectedLng);
};

// Ramer–Douglas–Peucker simplification: drops points within `tolerance`
// of the line between their neighbors; never invents new ones.
export const simplifyPolygonPath = (
  path: PolygonPath,
  tolerance: number
): PolygonPath => {
  if (path.length < 3) return path;

  const start = path[0];
  const end = path[path.length - 1];
  let maxDistance = 0;
  let maxIndex = 0;
  for (let i = 1; i < path.length - 1; i++) {
    const distance = perpendicularDistance(path[i], start, end);
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = i;
    }
  }

  if (maxDistance <= tolerance) {
    return [start, end];
  }

  const left = simplifyPolygonPath(path.slice(0, maxIndex + 1), tolerance);
  const right = simplifyPolygonPath(path.slice(maxIndex), tolerance);
  return [...left.slice(0, -1), ...right];
};

// Web Mercator meters-per-pixel at a given zoom/latitude — converts
// pixel-space UI thresholds into the polygon math's degree space.
const metersPerPixel = (zoom: number, latitude: number): number =>
  (156543.03392 * Math.cos((latitude * Math.PI) / 180)) / 2 ** zoom;

// Screen-pixel distance to lat/lng degrees. Approximate (flat
// ~111,320m/degree) — fine for a UI tolerance, not precise geodesy.
export const degreesForPixels = (
  pixels: number,
  zoom: number,
  latitude: number
): number => (pixels * metersPerPixel(zoom, latitude)) / 111320;

// 0/1/2 = collinear/clockwise/counterclockwise. Epsilon is 1e-16, not the
// "standard" 1e-12 — that falsely flagged freehand curves as collinear.
const orientation = (a: PolygonPoint, b: PolygonPoint, c: PolygonPoint) => {
  const val =
    (b.lng - a.lng) * (c.lat - b.lat) - (b.lat - a.lat) * (c.lng - b.lng);
  if (Math.abs(val) < 1e-16) return 0;
  return val > 0 ? 1 : 2;
};

const onSegment = (a: PolygonPoint, b: PolygonPoint, c: PolygonPoint) =>
  b.lat <= Math.max(a.lat, c.lat) &&
  b.lat >= Math.min(a.lat, c.lat) &&
  b.lng <= Math.max(a.lng, c.lng) &&
  b.lng >= Math.min(a.lng, c.lng);

const segmentsIntersect = (
  p1: PolygonPoint,
  p2: PolygonPoint,
  p3: PolygonPoint,
  p4: PolygonPoint
) => {
  const o1 = orientation(p1, p2, p3);
  const o2 = orientation(p1, p2, p4);
  const o3 = orientation(p3, p4, p1);
  const o4 = orientation(p3, p4, p2);

  if (o1 !== o2 && o3 !== o4) return true;

  if (o1 === 0 && onSegment(p1, p3, p2)) return true;
  if (o2 === 0 && onSegment(p1, p4, p2)) return true;
  if (o3 === 0 && onSegment(p3, p1, p4)) return true;
  if (o4 === 0 && onSegment(p3, p2, p4)) return true;

  return false;
};

// A self-intersecting polygon breaks the backend's point-in-polygon filter,
// silently widening matches — used to validate a ring before it's stored.
export const isSimplePolygon = (path: PolygonPath): boolean => {
  const n = path.length;
  if (n < 3) return false;

  for (let i = 0; i < n; i++) {
    const a1 = path[i];
    const a2 = path[(i + 1) % n];
    for (let j = i + 1; j < n; j++) {
      const isAdjacent = j === i + 1 || (i === 0 && j === n - 1);
      if (isAdjacent) continue;
      const b1 = path[j];
      const b2 = path[(j + 1) % n];
      if (segmentsIntersect(a1, a2, b1, b2)) return false;
    }
  }
  return true;
};

// Where segment a1-a2 crosses b1-b2, or null if they're parallel or the
// crossing falls outside either segment (touching at an endpoint doesn't count).
const segmentIntersectionPoint = (
  a1: PolygonPoint,
  a2: PolygonPoint,
  b1: PolygonPoint,
  b2: PolygonPoint
): PolygonPoint | null => {
  const d1x = a2.lng - a1.lng;
  const d1y = a2.lat - a1.lat;
  const d2x = b2.lng - b1.lng;
  const d2y = b2.lat - b1.lat;
  const denom = d1x * d2y - d1y * d2x;
  if (Math.abs(denom) < 1e-16) return null;

  const dx = b1.lng - a1.lng;
  const dy = b1.lat - a1.lat;
  const t = (dx * d2y - dy * d2x) / denom;
  const u = (dx * d1y - dy * d1x) / denom;
  if (t <= 0 || t >= 1 || u <= 0 || u >= 1) return null;

  return { lat: a1.lat + t * d1y, lng: a1.lng + t * d1x };
};

// Safety cap on splice iterations — a pathological, densely self-crossing
// scribble should bail out rather than loop indefinitely.
const MAX_LOOP_SPLITS = 500;

// Safety cap on recursive re-splitting (see splitIntoSimpleLoops) — each
// pass is expected to shrink the problem, this just bounds worst-case depth.
const MAX_SPLIT_DEPTH = 20;

// A single left-to-right pass: peels off one loop per crossing found. May
// leave a residual loop that isn't fully simple — the caller re-splits it.
const splitOnePass = (path: PolygonPath): PolygonPath[] => {
  const loops: PolygonPath[] = [];
  const stack: PolygonPoint[] = [path[0]];
  const closed = [...path, path[0]];
  let guard = 0;
  let i = 1;

  while (i < closed.length) {
    if (++guard > MAX_LOOP_SPLITS) break;
    const next = closed[i];
    const segStart = stack[stack.length - 1];

    let spliceIndex = -1;
    let intersection: PolygonPoint | null = null;
    // Skip the edge immediately before segStart — it shares that vertex,
    // which would otherwise register as a trivial "crossing" at t=0.
    for (let j = 0; j < stack.length - 2; j++) {
      const point = segmentIntersectionPoint(
        stack[j],
        stack[j + 1],
        segStart,
        next
      );
      if (point) {
        spliceIndex = j;
        intersection = point;
        break;
      }
    }

    if (intersection && spliceIndex >= 0) {
      const loop = [...stack.slice(spliceIndex + 1), intersection];
      if (loop.length >= 3) loops.push(loop);
      stack.length = spliceIndex + 1;
      stack.push(intersection);
      // Retry the same `next` against the shrunk stack — it may cross
      // an earlier edge too.
      continue;
    }

    stack.push(next);
    i++;
  }

  // The closing point duplicates path[0]; drop it before keeping the rest
  // as the final loop.
  const finalLoop = stack.slice(0, -1);
  if (finalLoop.length >= 3) loops.push(finalLoop);
  return loops;
};

// Splits a closed, possibly self-intersecting path into simple loops (e.g.
// a figure-8 becomes two rings), re-splitting any still-non-simple result.
export const splitIntoSimpleLoops = (
  path: PolygonPath,
  depth = 0
): PolygonPath[] => {
  if (path.length < 3) return [];

  const onePass = splitOnePass(path);
  // A single, same-length loop means no splice happened — further passes
  // would repeat identically, so stop instead of burning the depth budget.
  if (onePass.length === 1 && onePass[0].length === path.length) {
    return onePass;
  }

  return onePass.flatMap((loop) => {
    if (depth >= MAX_SPLIT_DEPTH || isSimplePolygon(loop)) return [loop];
    return splitIntoSimpleLoops(loop, depth + 1);
  });
};
